import { useSafeState } from "core/hooks";
import { getQueryVariable } from "core/model/utils/urls";
import { fromUnixTime, isAfter, subDays, subMinutes } from "date-fns";
import { List, Map } from "immutable";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLoggedInAccount } from "reduxentities/hooks";
import {
  TRANSLATION_ACTIONS,
  TranslationState,
  translationsReducer,
} from "./translationsHandler";
import {
  ComputedFormModel,
  FormModel,
  FormValue,
  Suggestion,
  TranslationTypes,
} from "./types";
import {
  arrayToState,
  convertToDbArray,
  getEmptySelection,
  indexOfSeparator,
  lastIndexOfSeparator,
} from "./utils";

export function useItemQueryParam(
  translations: Map<string, any> | null | undefined,
  dispatch: (arg: any) => void,
) {
  const location = useLocation();
  const [loaded, setLoaded] = useSafeState(false);

  useEffect(() => {
    let timeout: number;

    if (!loaded)
      timeout = window.setTimeout(() => {
        if (location?.search) {
          if (!translations || translations.isEmpty()) return;
          const queryItem = getQueryVariable(location.search, "item");
          if (queryItem) {
            const imPath = queryItem.split(".");
            const item = translations.getIn(imPath);
            if (item != null)
              dispatch({
                payload: { selection: [queryItem, item.toJS()] },
                type: TRANSLATION_ACTIONS.SELECT,
              });
          }
          setLoaded(true);
        }
      }, 0);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [loaded, translations]);
}

export const useTranslationsEditor = ({
  onExport,
  translationsArray,
}: {
  onExport: (arg: any) => void;
  translationsArray: Readonly<TranslationTypes> | undefined;
}) => {
  const navigate = useNavigate();
  const [{ history, selection, translations }, dispatch]: [
    any,
    (v: any) => void,
  ] = useReducer(translationsReducer, {
    // To store in the backend ideally
    history: {
      all: List(),
      current: List(), // Just to enable translation deletion in-session
    },
    selection: getEmptySelection(),
    translations: arrayToState(translationsArray ?? []),
  } as TranslationState);

  useItemQueryParam(translations, dispatch);

  useEffect(() => {
    if (translationsArray != null)
      dispatch({
        payload: { translations: arrayToState(translationsArray) },
        type: TRANSLATION_ACTIONS.REFRESH,
      });
  }, [translationsArray]);

  useLayoutEffect(() => {
    if (selection?.[0] && navigate)
      navigate({ search: `?item=${selection[0]}` });
  }, [selection]);

  const exportTranslations = useCallback(() => {
    const dbArray = convertToDbArray(translations);
    onExport(dbArray);
  }, [translations]);

  return {
    dispatch,
    exportTranslations,
    history,
    selection,
    translations,
  };
};

const generateFormModel = (model: FormModel): ComputedFormModel =>
  Object.entries(model).reduce((form, [key, value]) => {
    if (typeof value !== "object" || Array.isArray(value))
      return { ...form, [key]: { error: null, validate: null, value } };
    return { ...form, [key]: { ...value, error: null } };
  }, {});

export const useForm = (
  model: FormModel,
  onSubmit: () => any,
  argInputs?: Array<any>,
) => {
  const [formState, setFormState] = useState(() => generateFormModel(model));
  const [validation, setValidation] = useState(List());
  const inputs = argInputs || [];

  const setFormValue = (key: string, value: FormValue) =>
    setFormState((state) => ({ ...state, [key]: { ...state[key], value } }));

  const validateForm = useCallback(
    () =>
      Object.entries(formState).reduce((acc, [key, data]) => {
        if (typeof data !== "object" || data == null) return acc;

        const { error, validate, value } = data;

        if (typeof validate === "function") {
          const fieldError = validate(value, formState);

          if (fieldError != error)
            setFormState((state) => ({
              ...state,
              [key]: { ...state[key], error: fieldError },
            }));

          if (fieldError != null && !validation.includes(key))
            setValidation((keys) => keys.push(key));
          else if (validation.includes(key))
            setValidation((keys) => keys.delete(keys.indexOf(key)));

          return fieldError != null ? false : acc;
        }
        return acc;
      }, true),
    [formState, validation],
  );

  useEffect(() => {
    if (validation.size > 0) validateForm();
  }, [validation, validateForm]);

  useEffect(() => {
    setFormState(generateFormModel(model));
  }, inputs);

  const submit = useCallback(() => {
    if (validateForm()) onSubmit();
  }, [onSubmit, validateForm]);

  return { formState, setFormValue, submit };
};

export const useAutocomplete = (
  value: FormValue,
  cursorPos: number,
  suggestions: Array<Suggestion> | null | undefined,
) => {
  const [autocomplete, setAutocomplete] = useState<Suggestion[]>([]);

  useEffect(() => {
    if (value && typeof value == "string" && suggestions) {
      setAutocomplete([]);

      const startAnchor = lastIndexOfSeparator(value.slice(0, cursorPos));
      const endAnchor = indexOfSeparator(value, cursorPos);

      const lastWord = value
        .slice(
          startAnchor === -1 ? 0 : startAnchor,
          endAnchor === -1 ? value.length : endAnchor,
        )
        .trim();

      if (lastWord)
        suggestions.forEach((suggestion) => {
          const length = lastWord.length;
          const triggerPart = suggestion.trigger.slice(0, length);
          if (lastWord === triggerPart)
            setAutocomplete((cur) => cur.concat(suggestion));
        });
    }
  }, [value, suggestions]);

  return autocomplete;
};

export function useInputCursorPos(id: string) {
  const [cursosPos, setPos] = useState<number>(0);

  function getPos(e: KeyboardEvent | MouseEvent) {
    setPos((e.target as HTMLInputElement).selectionStart ?? 0);
  }

  useEffect(() => {
    const el = document.getElementById(id);

    if (el != null) {
      el.addEventListener("keyup", getPos);
      el.addEventListener("click", getPos);
    }

    return () => {
      if (el != null) {
        el.removeEventListener("keyup", getPos);
        el.removeEventListener("click", getPos);
      }
    };
  });

  return cursosPos;
}

export function useCanDeleteTranslation({
  createdAt,
}: {
  createdAt: number | undefined;
}) {
  const account = useLoggedInAccount();

  const devs = [
    "miguel.pereira",
    "william.taylor",
    "kim.andersen",
    "mehdi.bouzida",
    "michael.uebele",
  ];

  if (devs.some((username) => account?.email?.startsWith(username)))
    return true;
  if (
    createdAt != null &&
    isAfter(fromUnixTime(createdAt), subDays(subMinutes(new Date(), 30), 1))
  )
    return true;

  return false;
}
